// TEMPORARY: Sales Ops field overrides
// This file overrides the default schema field order and required status
// To remove: Delete this file and remove imports/usage from Search.tsx

import type { IntakePortalSchema, IntakePortalSchemaField } from '@/models/types';

// Fields in desired order with required status
const SALES_OPS_FIELD_CONFIG: Array<{ key: string; required: boolean }> = [
  // Required fields
  { key: 'name', required: true },
  { key: 'street_address', required: true },
  { key: 'city', required: true },
  { key: 'state', required: true },
  { key: 'zip_code', required: true },
  { key: 'pmc', required: true },
  { key: 'total_units', required: true },
  { key: 'pms', required: true },
  { key: 'crm', required: true },
  { key: 'invoice_recipient', required: true },
  { key: 'invoice_submission_type', required: true },
  // Optional fields (at end)
  { key: 'true_owner', required: false },
  { key: 'property_website', required: false },
  { key: 'ownership_group', required: false },
];

export const SALES_OPS_ENABLED = true; // Toggle to disable overrides

// Fields that are always visible: false in the standard/default schema.
// If any of these are visible: true, the org has a custom schema.
const STANDARD_HIDDEN_KEYS = new Set([
  'salesforce_id', 'product', 'contract_signer',
  'operational_team_member', 'marketing_team_member', 'implementation_team_member',
  'secondary_invoice_recipient', 'regional_supervisor',
  'regional_marketing_supervisor', 'regional_vice_president',
  'client_billing_id', 'client_property_id',
]);

export function applySalesOpsOverrides(schema: IntakePortalSchema): IntakePortalSchema {
  if (!SALES_OPS_ENABLED || !schema.intake_fields) {
    return schema;
  }

  // Detect custom schemas: if any normally-hidden field has been turned visible,
  // the backend has org-specific overrides — trust it as-is.
  const isCustomSchema = schema.intake_fields.some(
    (f) => STANDARD_HIDDEN_KEYS.has(f.key) && f.visible
  );
  if (isCustomSchema) {
    return schema;
  }

  // Build maps from config
  const orderMap = new Map<string, number>();
  const requiredMap = new Map<string, boolean>();
  const allowedKeys = new Set<string>();

  SALES_OPS_FIELD_CONFIG.forEach((f, index) => {
    orderMap.set(f.key, index);
    requiredMap.set(f.key, f.required);
    allowedKeys.add(f.key);
  });

  // Transform fields - hide fields not in our list
  const transformedFields = schema.intake_fields.map((field): IntakePortalSchemaField => {
    const isAllowed = allowedKeys.has(field.key);

    if (!isAllowed) {
      // Hide fields not in our list
      return { ...field, visible: false };
    }

    return {
      ...field,
      order: orderMap.get(field.key) ?? field.order,
      required: requiredMap.get(field.key) ?? field.required,
      // Clear soft_required for fields we're making required
      soft_required: requiredMap.get(field.key) === true ? false : field.soft_required,
    };
  });

  return {
    ...schema,
    intake_fields: transformedFields,
  };
}
